import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Avatar,
  Alert,
  Divider,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Payment,
  CreditCard,
  AccountBalanceWallet,
  QrCode2,
  TrendingUp,
  TrendingDown,
  MoreVert,
  Add,
  CheckCircle,
  Pending,
  Cancel,
  Receipt,
  Security,
  Refresh,
  Download,
  Visibility,
} from '@mui/icons-material';
import { Pedido, getPedidos } from '../../../services/pedidos';

// Styled Components con diseño El Piñón
const DashboardContainer = styled(Box)({
  background: 'linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%)',
  minHeight: '100vh',
  padding: '24px',
  '@media (max-width: 768px)': {
    padding: '16px',
  },
});

const HeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '32px',
  padding: '20px',
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  borderRadius: '20px',
  color: 'white',
  boxShadow: '0 8px 32px rgba(46, 90, 51, 0.3)',
  flexWrap: 'wrap',
  gap: '16px',
  '@media (max-width: 768px)': {
    padding: '16px',
    marginBottom: '24px',
  },
});

const StatsCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '2px solid #e8f5e8',
  boxShadow: '0 4px 20px rgba(46, 90, 51, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(46, 90, 51, 0.2)',
    borderColor: '#2e5a33',
  },
});

const PaymentCard = styled(Card)({
  background: 'linear-gradient(135deg, #fff 0%, #fdfaf4 100%)',
  borderRadius: '16px',
  border: '1px solid #e8f5e8',
  marginBottom: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 25px rgba(46, 90, 51, 0.15)',
  },
  '@media (max-width: 600px)': {
    '& .MuiCardContent-root': {
      padding: '16px',
    },
  },
});

const ActionButton = styled(Button)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  color: 'white',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3d7342 0%, #2e5a33 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 90, 51, 0.4)',
  },
});

const DashboardPagos: React.FC = () => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePaymentMethod, setActivePaymentMethod] = useState<string>('todas');
  const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  // Función para generar y descargar boleta PDF
  const descargarBoleta = (pedido: Pedido) => {
    const fechaPedido = new Date(pedido.fecha_pedido).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const itemsRows = pedido.items?.map(item => `
      <tr>
        <td style="padding: 12px; border-bottom: 1px solid #eee;">${item.producto_detalle?.nombre || item.producto_nombre || 'Producto'}</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: center;">${item.cantidad}</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: right;">${formatCurrency(item.precio_unitario)}</td>
        <td style="padding: 12px; border-bottom: 1px solid #eee; text-align: right; font-weight: 600;">${formatCurrency(item.precio_total)}</td>
      </tr>
    `).join('') || '';

    const boletaHTML = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Boleta ${pedido.numero_pedido} - El Piñón</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            padding: 40px; 
            background: #f5f5f5;
            color: #333;
          }
          .boleta-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          }
          .header {
            background: linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%);
            color: white;
            padding: 35px;
            text-align: center;
          }
          .header h1 {
            font-size: 32px;
            margin-bottom: 8px;
            font-weight: 700;
          }
          .header .tipo-doc {
            font-size: 14px;
            opacity: 0.9;
            text-transform: uppercase;
            letter-spacing: 2px;
          }
          .boleta-info {
            display: flex;
            justify-content: space-around;
            padding: 25px;
            background: linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%);
            border-bottom: 2px solid #e8f5e8;
          }
          .boleta-info div {
            text-align: center;
          }
          .boleta-info label {
            font-size: 11px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-weight: 600;
          }
          .boleta-info p {
            font-size: 16px;
            font-weight: 700;
            color: #2e5a33;
            margin-top: 6px;
          }
          .seccion {
            padding: 25px 30px;
          }
          .seccion h3 {
            color: #2e5a33;
            margin-bottom: 18px;
            font-size: 16px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .cliente-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            background: #f9f9f9;
            padding: 20px;
            border-radius: 12px;
          }
          .cliente-grid div label {
            font-size: 11px;
            color: #888;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .cliente-grid div p {
            font-size: 14px;
            color: #333;
            margin-top: 4px;
            font-weight: 500;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          thead tr {
            background: linear-gradient(135deg, #2e5a33 0%, #4a7c59 100%);
          }
          th {
            padding: 14px 12px;
            text-align: left;
            font-size: 12px;
            text-transform: uppercase;
            color: white;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          th:nth-child(2), th:nth-child(3) {
            text-align: center;
          }
          th:last-child {
            text-align: right;
          }
          tbody tr:hover {
            background: #f8fffe;
          }
          .total-section {
            padding: 25px 30px;
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .total-section span {
            color: rgba(255,255,255,0.8);
            font-size: 16px;
            font-weight: 500;
          }
          .total-section .monto {
            font-size: 32px;
            font-weight: 700;
            color: #4ade80;
          }
          .estado-pago {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            border-radius: 50px;
            font-size: 13px;
            font-weight: 600;
          }
          .estado-pagado {
            background: rgba(74, 222, 128, 0.2);
            color: #4ade80;
          }
          .estado-procesando {
            background: rgba(255, 152, 0, 0.2);
            color: #ff9800;
          }
          .estado-pendiente {
            background: rgba(251, 191, 36, 0.2);
            color: #fbbf24;
          }
          .footer {
            padding: 25px 30px;
            text-align: center;
            background: #f8fffe;
            font-size: 13px;
            color: #666;
          }
          .footer p {
            margin: 6px 0;
          }
          .footer .gracias {
            font-size: 16px;
            font-weight: 600;
            color: #2e5a33;
            margin-bottom: 10px;
          }
          @media print {
            body { padding: 0; background: white; }
            .boleta-container { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="boleta-container">
          <div class="header">
            <h1>🌲 El Piñón</h1>
            <p class="tipo-doc">Boleta de Compra</p>
          </div>

          <div class="boleta-info">
            <div>
              <label>Nº Pedido</label>
              <p>#${pedido.numero_pedido}</p>
            </div>
            <div>
              <label>Fecha</label>
              <p>${fechaPedido}</p>
            </div>
            <div>
              <label>Método de Pago</label>
              <p>${pedido.metodo_pago.toUpperCase()}</p>
            </div>
            <div>
              <label>Estado de Pago</label>
              <p>
                <span class="estado-pago ${
                  pedido.pagado && pedido.estado_pago === 'aprobado' 
                    ? 'estado-pagado' 
                    : pedido.estado_pago === 'procesando' && !pedido.pagado
                      ? 'estado-procesando'
                      : 'estado-pendiente'
                }">
                  ${
                    pedido.pagado && pedido.estado_pago === 'aprobado'
                      ? '✓ Pagado'
                      : pedido.estado_pago === 'procesando' && !pedido.pagado
                        ? '⏳ Procesando'
                        : '⏳ Pendiente'
                  }
                </span>
              </p>
            </div>
          </div>

          <div class="seccion">
            <h3>👤 Datos del Cliente</h3>
            <div class="cliente-grid">
              <div>
                <label>Nombre Completo</label>
                <p>${pedido.cliente_detalle ? `${pedido.cliente_detalle.nombre || ''} ${pedido.cliente_detalle.apellido || ''}`.trim() || 'Cliente' : 'Cliente'}</p>
              </div>
              <div>
                <label>Email</label>
                <p>${pedido.cliente_detalle?.email || '-'}</p>
              </div>
              <div>
                <label>Teléfono</label>
                <p>${pedido.direccion_entrega_detalle?.telefono_contacto || pedido.cliente_detalle?.telefono || '-'}</p>
              </div>
              <div>
                <label>RUT/DNI</label>
                <p>${pedido.cliente_detalle?.ruc || pedido.cliente_detalle?.dni || '-'}</p>
              </div>
              <div style="grid-column: span 2;">
                <label>Dirección de Entrega</label>
                <p>${pedido.direccion_entrega_detalle ? `${pedido.direccion_entrega_detalle.direccion_linea1}${pedido.direccion_entrega_detalle.direccion_linea2 ? ', ' + pedido.direccion_entrega_detalle.direccion_linea2 : ''}, ${pedido.direccion_entrega_detalle.distrito || ''} ${pedido.direccion_entrega_detalle.ciudad}`.trim() : '-'}</p>
              </div>
            </div>
          </div>

          <div class="seccion">
            <h3>🛒 Detalle de Productos</h3>
            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Precio Unit.</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                ${itemsRows || '<tr><td colspan="4" style="text-align: center; padding: 20px; color: #999;">Sin productos detallados</td></tr>'}
              </tbody>
            </table>
          </div>

          <div class="total-section">
            <span>TOTAL A PAGAR</span>
            <span class="monto">${formatCurrency(pedido.total)}</span>
          </div>

          <div class="footer">
            <p class="gracias">¡Gracias por tu compra!</p>
            <p>www.elpinon.almacenlocal.com</p>
            ${pedido.codigo_seguimiento ? `<p style="margin-top: 10px;">Código de seguimiento: <strong>${pedido.codigo_seguimiento}</strong></p>` : ''}
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(boletaHTML);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    } else {
      alert('Por favor habilita las ventanas emergentes para descargar la boleta');
    }
  };

  // Cargar pedidos desde la API
  const cargarPedidos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPedidos();
      setPedidos(data);
    } catch (err: any) {
      console.error('Error al cargar pedidos:', err);
      setError(err.message || 'Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPedidos();
  }, []);

  // Auto-refresh cada 30 segundos
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(cargarPedidos, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Formatear precio
  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(numPrice);
  };

  // Calcular estadísticas desde pedidos reales
  const hoy = new Date().toDateString();
  const pedidosHoy = pedidos.filter(p => new Date(p.fecha_pedido).toDateString() === hoy);
  const pedidosPagados = pedidos.filter(p => p.pagado);
  const pedidosPendientes = pedidos.filter(p => !p.pagado && p.estado !== 'cancelado');
  
  const ventasHoy = pedidosHoy.reduce((sum, p) => sum + parseFloat(p.total), 0);
  const totalTransacciones = pedidosPagados.length;
  const pagosPendientes = pedidosPendientes.reduce((sum, p) => sum + parseFloat(p.total), 0);
  
  // Calcular comisiones (ejemplo: 3.5% de MercadoPago)
  const pedidosMercadoPago = pedidosPagados.filter(p => p.metodo_pago === 'mercadopago');
  const comisiones = pedidosMercadoPago.reduce((sum, p) => sum + (parseFloat(p.total) * 0.035), 0);

  const statsData = [
    {
      title: 'Ventas Hoy',
      value: formatPrice(ventasHoy),
      change: '+12.5%',
      trend: 'up',
      icon: <TrendingUp />,
      color: '#2e5a33',
    },
    {
      title: 'Transacciones',
      value: totalTransacciones.toString(),
      change: '+8.2%',
      trend: 'up',
      icon: <Payment />,
      color: '#3d7342',
    },
    {
      title: 'Pagos Pendientes',
      value: formatPrice(pagosPendientes),
      change: pedidosPendientes.length > 0 ? '-3.1%' : '0%',
      trend: pedidosPendientes.length > 0 ? 'down' : 'up',
      icon: <Pending />,
      color: '#ff8f00',
    },
    {
      title: 'Comisiones',
      value: formatPrice(comisiones),
      change: '+15.3%',
      trend: 'up',
      icon: <AccountBalanceWallet />,
      color: '#2e7d32',
    },
  ];

  // Filtrar pedidos por método de pago
  const pedidosFiltrados = activePaymentMethod === 'todas' 
    ? pedidos 
    : pedidos.filter(p => p.metodo_pago === activePaymentMethod);

  // Calcular paginación
  const totalPages = Math.ceil(pedidosFiltrados.length / ITEMS_PER_PAGE);
  const paginatedPedidos = pedidosFiltrados.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // Resetear página cuando cambia el filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [activePaymentMethod]);

  const getStatusColor = (pagado: boolean, estado: string, estadoPago?: string) => {
    if (estado === 'cancelado') return '#d32f2f';
    // Si está pagado Y aprobado, mostrar verde (pagado tiene prioridad)
    if (pagado && estadoPago === 'aprobado') return '#2e7d32';
    // Si solo está procesando (no pagado aún)
    if (estadoPago === 'procesando' && !pagado) return '#ff9800';
    return pagado ? '#2e7d32' : '#ff8f00';
  };

  const getStatusIcon = (pagado: boolean, estado: string, estadoPago?: string) => {
    if (estado === 'cancelado') return <Cancel />;
    // Si está pagado, mostrar check
    if (pagado && estadoPago === 'aprobado') return <CheckCircle />;
    // Si solo está procesando
    if (estadoPago === 'procesando' && !pagado) return <Pending />;
    return pagado ? <CheckCircle /> : <Pending />;
  };

  const getStatusText = (pagado: boolean, estado: string, estadoPago?: string) => {
    if (estado === 'cancelado') return 'Cancelado';
    // Si está pagado Y aprobado, mostrar Pagado
    if (pagado && estadoPago === 'aprobado') return 'Pagado';
    // Si solo está procesando
    if (estadoPago === 'procesando' && !pagado) return 'Procesando';
    return pagado ? 'Pagado' : 'Pendiente';
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'tarjeta': return <CreditCard />;
      case 'mercadopago': return <QrCode2 />;
      case 'transferencia': return <AccountBalanceWallet />;
      case 'efectivo': return <Payment />;
      case 'yape': 
      case 'plin':
      case 'digital': return <QrCode2 />;
      default: return <Payment />;
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(numAmount);
  };

  const formatTime = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <DashboardContainer>
      {/* Header */}
      <HeaderSection>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src="/assets/logos/logo-el-pinon-nuevo.png"
            sx={{ width: 60, height: 60, border: '3px solid rgba(255,255,255,0.3)' }}
          />
          <Box>
            <Typography variant="h4" fontWeight={700} fontFamily="'Playfair Display', serif">
              Pagos Online - El Piñón
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Gestión de pagos y transacciones en tiempo real
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#fff',
                  },
                }}
              />
            }
            label="Auto-refresh"
            sx={{ color: 'white' }}
          />
          <ActionButton
            startIcon={<Add />}
            onClick={() => setShowNewPaymentForm(true)}
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            Nueva Transacción
          </ActionButton>
        </Box>
      </HeaderSection>

      {/* Stats Cards */}
      <Box display="flex" gap={3} marginBottom={4} flexWrap="wrap">
        {statsData.map((stat, index) => (
          <Box key={index} flex="1 1 250px">
            <StatsCard>
              <CardContent sx={{ padding: '24px' }}>
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" marginBottom={1}>
                      {stat.title}
                    </Typography>
                    <Chip
                      label={stat.change}
                      size="small"
                      sx={{
                        backgroundColor: stat.trend === 'up' ? '#e8f5e9' : '#fff3e0',
                        color: stat.trend === 'up' ? '#2e7d32' : '#f57c00',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Avatar
                    sx={{
                      backgroundColor: stat.color,
                      color: 'white',
                      width: 56,
                      height: 56,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </StatsCard>
          </Box>
        ))}
      </Box>

      {/* Payment Methods Filter */}
      <Box marginBottom={3}>
        <Typography variant="h6" fontWeight={600} marginBottom={2} color="#2e5a33">
          Filtrar por método de pago
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {['todas', 'mercadopago', 'tarjeta', 'transferencia', 'efectivo', 'yape', 'plin'].map((method) => (
            <Chip
              key={method}
              label={method === 'todas' ? 'Todas' : method.charAt(0).toUpperCase() + method.slice(1)}
              onClick={() => setActivePaymentMethod(method)}
              variant={activePaymentMethod === method ? 'filled' : 'outlined'}
              sx={{
                backgroundColor: activePaymentMethod === method ? '#2e5a33' : 'transparent',
                color: activePaymentMethod === method ? 'white' : '#2e5a33',
                borderColor: '#2e5a33',
                '&:hover': {
                  backgroundColor: activePaymentMethod === method ? '#3d7342' : 'rgba(46, 90, 51, 0.1)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            marginBottom: 3,
            borderRadius: '12px',
          }}
        >
          {error}
        </Alert>
      )}

      {/* Alert de seguridad */}
      <Alert
        severity="info"
        sx={{
          marginBottom: 3,
          borderRadius: '12px',
          backgroundColor: '#e3f2fd',
          border: '1px solid #90caf9',
        }}
        icon={<Security />}
      >
        <Typography variant="body2">
          Todas las transacciones están protegidas con encriptación de extremo a extremo. 
          Certificado SSL activo.
        </Typography>
      </Alert>

      {/* Loading State */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} sx={{ color: '#2e5a33' }} />
        </Box>
      ) : (
        <>
      {/* Transactions List */}
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
          <Typography variant="h6" fontWeight={600} color="#2e5a33">
            Transacciones Recientes ({pedidosFiltrados.length})
          </Typography>
          <Button
            startIcon={<Refresh />}
            onClick={cargarPedidos}
            sx={{ color: '#2e5a33' }}
          >
            Actualizar
          </Button>
        </Box>

        {paginatedPedidos.map((pedido) => (
            <PaymentCard key={pedido.id}>
              <CardContent sx={{ padding: { xs: '16px', md: '20px' } }}>
                <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  gap={{ xs: 2, sm: 0 }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                      sx={{
                        backgroundColor: '#e8f5e8',
                        color: '#2e5a33',
                        width: { xs: 40, md: 48 },
                        height: { xs: 40, md: 48 },
                      }}
                    >
                      {getMethodIcon(pedido.metodo_pago)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                        {pedido.cliente_detalle?.nombre || 'Cliente'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pedido #{pedido.numero_pedido} - {pedido.items?.length || 0} productos
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(pedido.fecha_pedido)} • {pedido.metodo_pago.toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box 
                    textAlign={{ xs: 'left', sm: 'right' }} 
                    display="flex" 
                    alignItems="center" 
                    gap={2}
                    width={{ xs: '100%', sm: 'auto' }}
                    justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                    sx={{
                      borderTop: { xs: '1px solid rgba(46, 90, 51, 0.1)', sm: 'none' },
                      paddingTop: { xs: 2, sm: 0 },
                      marginLeft: { xs: 0, sm: 0 },
                    }}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={700} color="#2e5a33" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                        {formatCurrency(pedido.total)}
                      </Typography>
                      <Chip
                        label={getStatusText(pedido.pagado, pedido.estado, pedido.estado_pago)}
                        size="small"
                        icon={getStatusIcon(pedido.pagado, pedido.estado, pedido.estado_pago)}
                        sx={{
                          backgroundColor: `${getStatusColor(pedido.pagado, pedido.estado, pedido.estado_pago)}15`,
                          color: getStatusColor(pedido.pagado, pedido.estado, pedido.estado_pago),
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <IconButton
                      onClick={() => descargarBoleta(pedido)}
                      title="Descargar Boleta"
                      sx={{
                        color: '#2e5a33',
                        bgcolor: 'rgba(46, 90, 51, 0.08)',
                        '&:hover': { 
                          backgroundColor: 'rgba(46, 90, 51, 0.15)',
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      <Receipt />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </PaymentCard>
          ))}

        {/* Paginación */}
        {pedidosFiltrados.length > ITEMS_PER_PAGE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, pedidosFiltrados.length)} de {pedidosFiltrados.length} transacciones
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 500,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                    color: 'white',
                  },
                },
              }}
            />
          </Box>
        )}

        {pedidosFiltrados.length === 0 && (
          <Box p={6} textAlign="center">
            <Avatar
              sx={{
                width: 80,
                height: 80,
                backgroundColor: '#e8f5e8',
                color: '#2e5a33',
                margin: '0 auto 16px',
              }}
            >
              <Payment sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h5" fontWeight={600} color="#2e5a33" marginBottom={2}>
              No se encontraron transacciones
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {activePaymentMethod !== 'todas' 
                ? 'No hay transacciones con este método de pago' 
                : 'No hay transacciones registradas'
              }
            </Typography>
          </Box>
        )}
      </Box>
      </>
      )}
    </DashboardContainer>
  );
};

export default DashboardPagos;